import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Alert, Button, Empty, Input, Modal, Pagination, Tooltip, message } from 'antd'
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  MobileOutlined,
  PictureOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  batchRerunWithTemplate,
  createAutoProcessTemplate,
  currentProcessingConfigOf,
  deleteChain,
  downloadChainsBatch,
  getUploadConfig,
  listChains,
  renameChain,
  rerunWithTemplate,
  retryChainStage,
  setPipelineApiScope,
  submitChainsFromPicker,
  triggerBrowserDownload,
  type AssetChainOut,
  type ChainListSummary,
  type UploadConfig,
} from '../../api/client'
import type { ImageAssetPickerSelection } from '../api'
import { ImageAssetPicker } from '../components/ImageAssetPicker'
import { notifyTemplateListChanged } from '../../utils/templateEvents'
import AutoProcessTemplateBar from '../../components/AutoProcessTemplateBar'
import RerunTemplateModal from '../../components/RerunTemplateModal'
import TemplateSaveDialog from '../../components/TemplateSaveDialog'
import UploadModal from '../../components/UploadModal'
import { FourStageFlowHeader } from '../../components/StageFlow'
import ChainEditorModal from '../../components/editor/ChainEditorModal'
import BackgroundEditorModal from '../../components/editor/BackgroundEditorModal'
import ShadowEditorModal from '../../components/editor/ShadowEditorModal'
import ChainRow from '../../components/ChainRow'
import StatusFilterTabs, { type StatusFilter } from '../../components/StatusFilterTabs'
import SelectionActionBar from '../../components/SelectionActionBar'
import RenameModal from '../../components/RenameModal'
import { useChainDeleteActions } from '../../components/useChainDeleteActions'
import { workspaceImagesBaseUrl } from '../api'
import MobileCaptureQrModal from '../components/MobileCaptureQrModal'
import { useWorkspaceState } from './WorkspaceContext'
import './image-asset-center.css'

// Active-chain polling (root-cause fix, live bug 2026-08): a fixed 2s poll
// of `GET .../chains?limit=3000` forever, even for a tenant sitting idle
// with everything already `done`, was flagged during a real cloud upload
// burst as needless sustained load on the same backend instance that was
// also proxying concurrent upload bytes (see `UploadModal` — this backend
// is proxying, not R2 direct, in production today) — one contributor to
// the 502s observed there. Once every chain has reached a terminal state
// (`done`/`failed`) there is nothing left to observe, so this backs off to
// a slow idle poll instead of hammering the list endpoint at a fixed rate
// regardless of activity.
const POLL_MS_ACTIVE = 2000
const POLL_MS_IDLE = 10000

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [20, 50, 100]

/** Mirrors `MAX_BATCH_DOWNLOAD_CHAINS` in
 * `backend/app/services/chain_batch_download.py` — checked here too so an
 * over-large selection is rejected instantly with a clear message instead
 * of round-tripping to the API first (spec 一.2). */
const MAX_BATCH_DOWNLOAD_CHAINS = 30

/** Target of the shared "选择自动处理模板重新处理" dialog — a single chain or
 * a multi-selected batch (no "group" case here: Browser Direct Upload
 * always creates loose/groupless chains, see `UploadsService.complete()`,
 * and folder import is intentionally not exposed to a SaaS tenant — spec
 * 十一 — a remote server has no local filesystem of the tenant's to read;
 * that capability stays reserved for a future Desktop Agent / Local
 * Worker mode instead). */
type RerunTarget = { kind: 'chain'; id: string; label: string } | { kind: 'batch'; ids: string[]; label: string }

/**
 * Phase 2B-2 — Pic2Content UI Recovery. This is the SaaS Workspace's
 * "Images" entry point, rebuilt to be functionally and visually the same
 * page as the standalone `Home.tsx` (spec: "老用户打开 SaaS Images
 * 后，应该基本感觉自己还在使用原来的 Pic2Content"), by composing the exact
 * same shared building blocks `Home.tsx` uses — `AutoProcessTemplateBar`,
 * `ChainRow`, `StatusFilterTabs`, `SelectionActionBar`, `RenameModal`,
 * `useChainDeleteActions`, `TemplateSaveDialog`, `RerunTemplateModal`, the
 * three editor modals — rather than a second, simplified reimplementation.
 * The only two things this page adds beyond `Home.tsx` are the
 * `setPipelineApiScope` Workspace-scoping effect below and the entitlement
 * Alert; the only two things it deliberately omits are folder import and
 * AssetGroup management (SaaS has no server-local filesystem and Browser
 * Direct Upload never produces groups — see `RerunTarget` above).
 */
export default function WorkspaceImagesPage() {
  const { t } = useTranslation(['autoProcess', 'actions', 'navigation', 'imageAssets'])
  const { workspace, entitlements, primarySite } = useWorkspaceState()
  const workspaceId = workspace?.id || ''
  const imageProcessingEnabled = Boolean(entitlements['feature.image_processing'])
  const primarySiteId = primarySite?.id || ''

  const [chains, setChains] = useState<AssetChainOut[]>([])
  const [summary, setSummary] = useState<ChainListSummary | null>(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<StatusFilter>('')
  const [selected, setSelected] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [batchDownloading, setBatchDownloading] = useState(false)

  const [uploadOpen, setUploadOpen] = useState(false)
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false)
  const [mobileCapturePolling, setMobileCapturePolling] = useState(false)
  const [assetPickerOpen, setAssetPickerOpen] = useState(false)
  const [assetPickerSubmitting, setAssetPickerSubmitting] = useState(false)
  const [uploadConfig, setUploadConfig] = useState<UploadConfig | null>(null)

  const [editorChain, setEditorChain] = useState<AssetChainOut | null>(null)
  const [backgroundEditorChain, setBackgroundEditorChain] = useState<AssetChainOut | null>(null)
  const [shadowEditorChain, setShadowEditorChain] = useState<AssetChainOut | null>(null)
  const [rerunTarget, setRerunTarget] = useState<RerunTarget | null>(null)
  const [saveAsTemplateChain, setSaveAsTemplateChain] = useState<AssetChainOut | null>(null)
  const [renameTarget, setRenameTarget] = useState<AssetChainOut | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // Scope the shared pipeline API client to THIS Workspace for as long as
  // this page is mounted (spec §14/§18) — restored to standalone `/api` on
  // unmount so no other still-mounted pipeline UI is ever left pointed at
  // someone else's Workspace.
  //
  // `scopeReady` deliberately gates every child below that fetches through
  // the shared pipeline client (`AutoProcessTemplateBar`, the chain list,
  // upload config): React always fires a *child*'s mount effect before its
  // *parent*'s, so if those children rendered unconditionally, their very
  // first fetch could race this effect and go out under the *previous*
  // Workspace's scope (or the standalone `/api` default) — and in
  // StrictMode dev (`main.tsx`), which synchronously mounts → cleans up →
  // remounts effects without re-rendering in between, an unconditional
  // child would even fire a *second*, still-unscoped request after this
  // effect's own cleanup nulls the scope back out and before the replayed
  // mount sets it again. Not rendering those children at all until this
  // effect has actually run closes both holes at once.
  const [scopeReady, setScopeReady] = useState(false)
  useEffect(() => {
    if (!workspaceId) return
    setPipelineApiScope(workspaceImagesBaseUrl(workspaceId))
    setScopeReady(true)
    return () => {
      setPipelineApiScope(null)
      setScopeReady(false)
    }
  }, [workspaceId])

  // Guards every poll tick (never the manual/explicit call sites below —
  // those are user-initiated and rare) against overlapping with a still-
  // in-flight previous request, so a slow/momentarily-degraded backend
  // response can never cause GETs to this endpoint to stack up faster than
  // they resolve (another contributor flagged in the same live upload-burst
  // investigation — see `POLL_MS_ACTIVE`/`POLL_MS_IDLE` above).
  const chainsPollInFlightRef = useRef(false)

  /** `pageOverride`/`pageSizeOverride` let a caller force an exact page
   * immediately (e.g. "jump to page 1 after a new upload") without waiting
   * for a `setPage` state update to actually commit and re-render first —
   * the closed-over `page`/`pageSize` state is only used as the default
   * for every other caller (manual refresh, the background poll). */
  // ASSET-UX-CONSISTENCY-A1: `loadChains` has several independent callers
  // (the background poll tick, an Editor's post-save `onSaved`, manual
  // refresh, search/page changes) whose HTTP responses are not guaranteed
  // to resolve in request order. `chainsPollInFlightRef` above only
  // prevents the poll from overlapping *itself* — it does nothing to stop
  // a poll response issued *before* an Editor save from landing *after*
  // that save's own refresh and silently reverting the row's
  // background/shadow_generation back to the stale value (Problem 3, Case
  // E). `requestSeq` makes only the most-recently *issued* call allowed to
  // commit state, independent of which caller made it.
  const requestSeqRef = useRef(0)
  const loadChains = useCallback(
    async (pageOverride?: number, pageSizeOverride?: number) => {
      if (!workspaceId || !scopeReady) return null
      const effectivePageSize = pageSizeOverride ?? pageSize
      const seq = ++requestSeqRef.current
      const { data } = await listChains({
        q: q || undefined,
        status: status || undefined,
        page: pageOverride ?? page,
        page_size: effectivePageSize,
      })
      if (seq !== requestSeqRef.current) return data.summary
      setChains(data.chains)
      setSummary(data.summary)
      if (data.page !== undefined) setPage(data.page)
      setPageSize(effectivePageSize)
      return data.summary
    },
    [workspaceId, scopeReady, q, status, page, pageSize],
  )

  // ASSET-UX-CONSISTENCY-A1: applied synchronously, in the same tick as the
  // save response, so the row shows the new background/shadow_generation
  // (and thus the busted URL) the instant the modal closes — never waiting
  // on a subsequent `loadChains()` round-trip to "happen to" land after it
  // and without needing to know which page the row is currently on.
  const mergeSavedChain = useCallback((updated: AssetChainOut) => {
    setChains((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
  }, [])

  useEffect(() => {
    if (!workspaceId || !scopeReady) return
    loadChains().catch(() => undefined)
    getUploadConfig()
      .then(({ data }) => setUploadConfig(data))
      .catch(() => undefined)
  }, [workspaceId, scopeReady, loadChains])

  // Search/status filter changes must never leave the user stranded on a
  // page number the new, narrower result set no longer has (spec 六.6).
  // `setStatus`/`setQ` and `setPage(1)` are always called together inside
  // the SAME synchronous event handler (React batches same-tick updates
  // into one re-render) so `loadChains` only ever gets recreated once,
  // with the new filter AND page=1 combined — never a transient re-render
  // with the new filter but the previous (possibly out-of-range) page,
  // which could otherwise briefly race a correct page-1 fetch with a
  // stale one for whatever page the user happened to be on before.
  const handleStatusChange = (v: StatusFilter) => {
    setStatus(v)
    setPage(1)
  }
  const handleSearchChange = (value: string) => {
    setQ(value)
    setPage(1)
  }

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    loadChains(nextPage, nextPageSize).catch(() => undefined)
  }

  useEffect(() => {
    if (!workspaceId || !scopeReady) return
    let cancelled = false
    let timer: number | undefined

    const tick = async () => {
      let nextDelay = POLL_MS_ACTIVE
      if (!chainsPollInFlightRef.current) {
        chainsPollInFlightRef.current = true
        try {
          const summaryResult = await loadChains()
          if (summaryResult) {
            const outstanding = summaryResult.total - summaryResult.done - summaryResult.failed
            nextDelay =
              outstanding > 0 || mobileCapturePolling ? POLL_MS_ACTIVE : POLL_MS_IDLE
          } else if (mobileCapturePolling) {
            nextDelay = POLL_MS_ACTIVE
          }
        } catch {
          // Transient poll failure — keep polling at the active rate rather
          // than silently settling into the slow/idle interval on a hiccup.
        } finally {
          chainsPollInFlightRef.current = false
        }
      }
      if (!cancelled) timer = window.setTimeout(tick, nextDelay)
    }

    timer = window.setTimeout(tick, POLL_MS_ACTIVE)
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [workspaceId, scopeReady, loadChains, mobileCapturePolling])

  // A multi-file upload's several near-simultaneous completions each used
  // to trigger their own immediate `loadChains()` — harmless for one file,
  // but a burst of N finishing within the same second (concurrency-limited
  // uploads, see `UploadModal`) meant up to N extra concurrent GETs to the
  // same list endpoint stacking on top of the regular poll, right as the
  // backend is also busiest proxying the remaining uploads' bytes. Collapse
  // any such burst into a single trailing refresh instead.
  const onAnyUploadCompletedRef = useRef<number | undefined>(undefined)
  const onAnyUploadCompleted = useCallback(() => {
    if (onAnyUploadCompletedRef.current !== undefined) window.clearTimeout(onAnyUploadCompletedRef.current)
    onAnyUploadCompletedRef.current = window.setTimeout(() => {
      onAnyUploadCompletedRef.current = undefined
      // Spec 六.7 — a newly-uploaded chain must be visible on page 1 of
      // the (newest-first) default sort without the user navigating back
      // there themselves. Explicit `loadChains(1, ...)` rather than just
      // `setPage(1)` so this fetches page 1 immediately even when the
      // user was already sitting on page 1 (no state change to react to
      // in that case).
      loadChains(1, pageSize).catch(() => undefined)
    }, 400)
  }, [loadChains, pageSize])
  useEffect(
    () => () => {
      if (onAnyUploadCompletedRef.current !== undefined) window.clearTimeout(onAnyUploadCompletedRef.current)
    },
    [],
  )

  // Sticky header (spec 四) — the toolbar/alerts/template-bar/status-tabs
  // block (`.pipeline-sticky-top`, wrapping this ref) is one continuous
  // `position: sticky` element with a natural (unconstrained) height, so
  // no measurement is needed for IT. The four-stage column-title row
  // right below it (`.pipeline-header-row`) also needs to stay pinned,
  // directly underneath, while staying grouped with `.pipeline-main`'s
  // data rows (so it renders as their column guide) — which means it
  // can't simply live *inside* the same sticky block above. Its `top`
  // offset must therefore equal that block's rendered height, which
  // changes (alerts appearing, the template drawer expanding, toolbar
  // wrapping on a narrow window) — measured live via ResizeObserver
  // rather than hardcoded, so it never drifts out of alignment.
  //
  // Environment/regression note (2026-08): `.pipeline-main` must never be
  // given an `overflow-x`/`overflow-y` rule again — any non-`visible`
  // overflow value on an ancestor turns it into a "scroll container",
  // which makes `position: sticky` on `.pipeline-header-row` resolve
  // against *that* box's own (never-scrolling) frame instead of the real
  // page scroll, permanently misplacing it `stickyTopHeight` px below its
  // static position — independent of actual scroll offset. See
  // `.pipeline-main`'s own CSS comment for the full incident writeup.
  const stickyTopRef = useRef<HTMLDivElement>(null)
  const [stickyTopHeight, setStickyTopHeight] = useState(0)
  useLayoutEffect(() => {
    const el = stickyTopRef.current
    if (!el) return
    const measure = () => setStickyTopHeight(el.offsetHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const toggleSelect = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const imageAssetsLabel = t('navigation:tenantNav.images.assets')
  const itemStandardLabel = t('navigation:tenantNav.images.itemStandard')

  const onRetryStage = async (id: string, stage: 'transparent' | 'background' | 'shadow') => {
    try {
      await retryChainStage(id, stage)
      message.info(t('toast.retryQueued'))
      await loadChains()
    } catch (e) {
      message.error((e as Error).message)
    }
  }

  const { onDeleteOriginal, onDeleteTransparent, onDeleteBackground, onDeleteShadow, onDeleteChain, deleteDialog } =
    useChainDeleteActions(
      async () => {
        await loadChains()
      },
      setChains,
      (id) => setSelected((prev) => prev.filter((x) => x !== id)),
    )

  const openRename = (chain: AssetChainOut) => {
    setRenameTarget(chain)
    setRenameValue(chain.original_filename.replace(/\.[^.]+$/, ''))
  }
  const confirmRename = async () => {
    if (!renameTarget || !renameValue.trim()) return
    try {
      await renameChain(renameTarget.id, renameValue.trim())
      message.success(t('toast.renameSuccess'))
      setRenameTarget(null)
      await loadChains()
    } catch (e) {
      message.error((e as Error).message)
    }
  }

  const batchAction = async (kind: 'retry-transparent' | 'reprocess' | 'delete') => {
    if (!selected.length) return
    if (kind === 'delete') {
      const ids = [...selected]
      Modal.confirm({
        // See centering note in `useChainDeleteActions.tsx` — static
        // Modal.confirm doesn't inherit ConfigProvider's `modal.centered`.
        centered: true,
        title: t('deleteActions.batchDeleteTitle', { count: ids.length }),
        content: t('deleteActions.batchDeleteContent'),
        okText: t('deleteActions.permanentDeleteOk'),
        okButtonProps: { danger: true },
        cancelText: t('actions:cancel'),
        // UI-first, same as the single-chain delete (`useChainDeleteActions`):
        // synchronous onOk so the modal closes and the selection/rows clear
        // immediately, while the actual deletes run in the background.
        onOk: () => {
          setChains((prev) => prev.filter((c) => !ids.includes(c.id)))
          setSelected([])
          ;(async () => {
            const failed: string[] = []
            for (const id of ids) {
              try {
                await deleteChain(id)
              } catch {
                failed.push(id)
              }
            }
            if (failed.length) {
              message.error(t('deleteActions.batchDeletePartial', { failed: failed.length, total: ids.length }))
              await loadChains()
            } else {
              message.success(t('deleteActions.batchDeleteSuccess'))
            }
          })()
        },
      })
      return
    }
    if (kind === 'reprocess') {
      setRerunTarget({ kind: 'batch', ids: [...selected], label: t('page.selectedCount', { count: selected.length }) })
      return
    }
    for (const id of selected) await retryChainStage(id, 'transparent').catch(() => undefined)
    message.success(t('toast.batchQueued', { count: selected.length }))
    setSelected([])
    await loadChains()
  }

  const handleBatchDownload = async () => {
    if (!selected.length || batchDownloading) return
    if (selected.length > MAX_BATCH_DOWNLOAD_CHAINS) {
      message.error(t('toast.batchDownloadLimit', { max: MAX_BATCH_DOWNLOAD_CHAINS }))
      return
    }
    setBatchDownloading(true)
    try {
      const { data } = await downloadChainsBatch(selected)
      // A ZIP blob has no `Content-Disposition` of its own (it's a local
      // `blob:` URL, no real HTTP response involved for the browser) —
      // unlike the single-asset download links, the filename here MUST
      // come from `triggerBrowserDownload`'s `download` attribute.
      const blobUrl = URL.createObjectURL(data)
      triggerBrowserDownload(blobUrl, 'images.zip')
      URL.revokeObjectURL(blobUrl)
    } catch {
      message.error(t('toast.batchDownloadFailed'))
    } finally {
      setBatchDownloading(false)
    }
  }

  const confirmRerun = async (templateId: string) => {
    if (!rerunTarget) return
    try {
      if (rerunTarget.kind === 'chain') {
        await rerunWithTemplate(rerunTarget.id, templateId)
      } else {
        await batchRerunWithTemplate(rerunTarget.ids, templateId)
        setSelected([])
      }
      message.success(t('toast.reprocessQueued'))
      setRerunTarget(null)
      await loadChains()
    } catch (e) {
      message.error((e as Error).message || t('toast.reprocessFailed'))
    }
  }

  const handleSaveAsTemplate = async (name: string, description: string) => {
    if (!saveAsTemplateChain) return
    try {
      await createAutoProcessTemplate({
        name,
        description: description || undefined,
        params: currentProcessingConfigOf(saveAsTemplateChain),
        set_active: false,
      })
      notifyTemplateListChanged()
      message.success(t('toast.saveTemplateSuccess', { filename: saveAsTemplateChain.original_filename, name }))
      setSaveAsTemplateChain(null)
    } catch (e) {
      message.error((e as Error).message || t('toast.saveTemplateFailed'))
    }
  }

  const openAssetPicker = () => {
    if (!primarySiteId) {
      message.warning(t('page.selectFromAssetLibraryNeedSite'))
      return
    }
    setAssetPickerOpen(true)
  }

  const handleAssetPickerConfirm = async (selections: ImageAssetPickerSelection[]) => {
    if (!selections.length || assetPickerSubmitting) return
    setAssetPickerSubmitting(true)
    try {
      const pickerItemIds = selections.map((s) => s.pickerItemId).filter(Boolean)
      const { data } = await submitChainsFromPicker(pickerItemIds)
      setAssetPickerOpen(false)
      message.success(t('page.selectFromAssetLibrarySuccess', { count: data.count ?? pickerItemIds.length }))
      await loadChains(1, pageSize)
    } catch (e) {
      message.error((e as Error).message || t('page.selectFromAssetLibraryFailed'))
    } finally {
      setAssetPickerSubmitting(false)
    }
  }

  if (!workspaceId) return null

  return (
    <div className="pipeline-page">
      <div className="pipeline-sticky-top" ref={stickyTopRef}>
        <div className="iac-process-crumb">
          <Link to={`/workspace/${workspaceId}/images`}>{imageAssetsLabel}</Link>
          <span className="sep">›</span>
          <span>{itemStandardLabel}</span>
          <span className="sep">·</span>
          <Link to={`/workspace/${workspaceId}/images`}>
            <ArrowLeftOutlined /> {t('page.backToAssets')}
          </Link>
        </div>
        <div className="pipeline-toolbar">
          <div className="toolbar-section toolbar-section-left">
            <div className="page-heading">
              <div className="page-title">{itemStandardLabel}</div>
              <div className="page-subtitle">{t('page.subtitle')}</div>
            </div>
          </div>
          <div className="toolbar-section toolbar-section-center">
            <Tooltip title={imageProcessingEnabled ? '' : t('imageAssets:toast.imageProcessingDisabled')}>
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                disabled={!imageProcessingEnabled || !uploadConfig?.browser_upload_enabled}
                onClick={() => setUploadOpen(true)}
                data-testid="saas-open-upload-btn"
              >
                {t('page.browserUpload')}
              </Button>
            </Tooltip>
            <Tooltip title={imageProcessingEnabled ? '' : t('imageAssets:toast.imageProcessingDisabled')}>
              <Button
                icon={<MobileOutlined />}
                disabled={!imageProcessingEnabled || !uploadConfig?.browser_upload_enabled}
                onClick={() => setMobileCaptureOpen(true)}
                data-testid="saas-open-mobile-capture-btn"
              >
                {t('page.mobileUpload')}
              </Button>
            </Tooltip>
            <Tooltip
              title={
                !imageProcessingEnabled
                  ? t('imageAssets:toast.imageProcessingDisabled')
                  : !primarySiteId
                    ? t('page.selectFromAssetLibraryNeedSite')
                    : ''
              }
            >
              <Button
                icon={<PictureOutlined />}
                disabled={!imageProcessingEnabled || !primarySiteId || assetPickerSubmitting}
                onClick={openAssetPicker}
                data-testid="saas-open-asset-library-picker-btn"
              >
                {t('page.selectFromAssetLibrary')}
              </Button>
            </Tooltip>
            <Tooltip title={t('actions:refresh')}>
              <Button icon={<ReloadOutlined />} onClick={() => loadChains()} />
            </Tooltip>
            <Input
              placeholder={t('page.searchPlaceholder')}
              prefix={<SearchOutlined />}
              style={{ width: 220 }}
              value={q}
              onChange={(e) => handleSearchChange(e.target.value)}
              onPressEnter={() => loadChains()}
              allowClear
            />
          </div>
          <div className="toolbar-section toolbar-section-right">
            {summary && (
              <span className="summary-text">
                {t('page.summary', {
                  total: summary.total,
                  done: summary.done,
                  processing: summary.processing,
                  failed: summary.failed,
                })}
              </span>
            )}
          </div>
        </div>

        {!imageProcessingEnabled && (
          <Alert
            type="warning"
            showIcon
            style={{ margin: '0 0 12px' }}
            message={t('page.featureDisabledAlert')}
          />
        )}
        {uploadConfig && !uploadConfig.browser_upload_enabled && (
          <Alert type="info" showIcon style={{ margin: '0 0 12px' }} message={t('page.browserUploadDisabledAlert')} />
        )}

        {/* Gated on scopeReady (not just workspaceId) — see the scope-switch
         * effect above for why an unconditionally-rendered child here could
         * otherwise fire its first template fetch under the wrong scope. */}
        {scopeReady && <AutoProcessTemplateBar />}

        <StatusFilterTabs value={status} onChange={handleStatusChange} />
      </div>

      <div className="pipeline-body">
        <div className="pipeline-main" style={{ width: '100%' }}>
          <SelectionActionBar
            count={selected.length}
            onRetryTransparent={() => batchAction('retry-transparent')}
            onReprocess={() => batchAction('reprocess')}
            onDelete={() => batchAction('delete')}
            onClear={() => setSelected([])}
            onBatchDownload={handleBatchDownload}
            batchDownloading={batchDownloading}
          />

          {/* `top` tracks the sticky block above (spec 四: "不要做成多层
           * sticky 互相覆盖") — its height varies (alerts appearing, the
           * auto-process template drawer expanding, narrow-viewport
           * wrapping), so it's measured at runtime via ResizeObserver
           * rather than hardcoded, see `stickyTopRef`/`stickyTopHeight`. */}
          <div className="pipeline-header-row" style={{ top: stickyTopHeight }}>
            <div className="pipeline-header-select" />
            <FourStageFlowHeader />
            <div className="pipeline-header-actions">{t('page.actionsColumn')}</div>
          </div>

          {chains.length === 0 ? (
            <Empty description={t('page.empty')} style={{ marginTop: 80 }} />
          ) : (
            chains.map((c) => (
              <ChainRow
                key={c.id}
                chain={c}
                selected={selected.includes(c.id)}
                onToggle={() => toggleSelect(c.id)}
                onEdit={() => setEditorChain(c)}
                onEditBackground={() => setBackgroundEditorChain(c)}
                onEditShadow={() => setShadowEditorChain(c)}
                onRetryStage={(stage) => onRetryStage(c.id, stage)}
                onDeleteOriginal={() => onDeleteOriginal(c)}
                onDeleteTransparent={() => onDeleteTransparent(c)}
                onDeleteBackground={() => onDeleteBackground(c)}
                onDeleteShadow={() => onDeleteShadow(c)}
                onDeleteChain={() => onDeleteChain(c)}
                onRename={() => openRename(c)}
                onReprocess={() => setRerunTarget({ kind: 'chain', id: c.id, label: t('page.selectedChainLabel', { filename: c.original_filename }) })}
                onSaveAsTemplate={() => setSaveAsTemplateChain(c)}
              />
            ))
          )}

          {chains.length > 0 && (
            <div className="pipeline-pagination-row">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={summary?.total ?? 0}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                showSizeChanger
                showTotal={(total) => t('page.paginationTotal', { total })}
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <UploadModal open={uploadOpen} config={uploadConfig} onClose={() => setUploadOpen(false)} onAnyCompleted={onAnyUploadCompleted} />

      <MobileCaptureQrModal
        open={mobileCaptureOpen}
        workspaceId={workspaceId}
        targetFlow="item_image_processing"
        onClose={() => setMobileCaptureOpen(false)}
        onSessionActiveChange={setMobileCapturePolling}
      />

      {primarySiteId ? (
        <ImageAssetPicker
          open={assetPickerOpen}
          workspaceId={workspaceId}
          siteId={primarySiteId}
          purpose="object_image_processing"
          selectionMode="multiple"
          maxSelection={20}
          title={t('page.selectFromAssetLibraryTitle')}
          initialSelection={[]}
          onCancel={() => setAssetPickerOpen(false)}
          onConfirm={(selections) => {
            void handleAssetPickerConfirm(selections)
          }}
        />
      ) : null}

      {deleteDialog}

      <RenameModal
        open={!!renameTarget}
        title={t('page.renameTitle')}
        value={renameValue}
        onChange={setRenameValue}
        onOk={confirmRename}
        onCancel={() => setRenameTarget(null)}
      />

      {editorChain && (
        <ChainEditorModal
          chainId={editorChain.id}
          filename={editorChain.original_filename}
          open={!!editorChain}
          initialChain={editorChain}
          onClose={() => setEditorChain(null)}
          onSaved={(updated) => {
            mergeSavedChain(updated)
            loadChains()
          }}
        />
      )}
      {backgroundEditorChain && backgroundEditorChain.transparent_url && (
        <BackgroundEditorModal
          chainId={backgroundEditorChain.id}
          filename={backgroundEditorChain.original_filename}
          transparentUrl={backgroundEditorChain.transparent_url}
          initialParams={backgroundEditorChain.background_params}
          open={!!backgroundEditorChain}
          onClose={() => setBackgroundEditorChain(null)}
          onSaved={(updated) => {
            mergeSavedChain(updated)
            loadChains()
          }}
        />
      )}
      {shadowEditorChain && shadowEditorChain.transparent_url && (
        <ShadowEditorModal
          chainId={shadowEditorChain.id}
          filename={shadowEditorChain.original_filename}
          transparentUrl={shadowEditorChain.transparent_url}
          backgroundParams={shadowEditorChain.background_params}
          initialShadowParams={shadowEditorChain.shadow_params}
          open={!!shadowEditorChain}
          onClose={() => setShadowEditorChain(null)}
          onSaved={(updated) => {
            mergeSavedChain(updated)
            loadChains()
          }}
        />
      )}

      <RerunTemplateModal
        open={!!rerunTarget}
        title={rerunTarget ? `${t('page.rerunTitlePrefix')}${rerunTarget.label}` : ''}
        originalOptionLabel={rerunTarget?.kind === 'batch' ? t('page.rerunOriginalBatch') : t('page.rerunOriginalSingle')}
        onCancel={() => setRerunTarget(null)}
        onConfirm={confirmRerun}
      />

      <TemplateSaveDialog
        open={!!saveAsTemplateChain}
        initialName={saveAsTemplateChain ? saveAsTemplateChain.original_filename.replace(/\.[^.]+$/, '') + t('page.templateNameSuffix') : ''}
        onCancel={() => setSaveAsTemplateChain(null)}
        onSubmit={handleSaveAsTemplate}
      />
    </div>
  )
}
